import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Check, MessageCircle, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, IMAGES } from "../../lib/api";
import { Reveal, EASE } from "../motion";
import { useLang, FR_DAYS_IDX, FR_MONTHS_IDX } from "../../lib/i18n";

const StepLabel = ({ num, label, active }) => (
  <div className={`flex items-center gap-3 ${active ? "text-espresso" : "text-espresso/40"}`}>
    <span className={`font-display text-lg ${active ? "text-taupe" : ""}`}>{num}</span>
    <span className="text-[12px] tracking-[0.2em] uppercase">{label}</span>
  </div>
);

const FAMILY_META = [
  { id: "laser", img: IMAGES.laser },
  { id: "electrolyse", img: "/images/electrolyse-2.jpg" },
  { id: "cil", img: "/images/lashlift-real.jpg" },
  { id: "brow", img: "/images/browlift-real.jpg" },
];

function familyOf(s) {
  const name = (s.name || "").toLowerCase();
  const cat = (s.category || "").toLowerCase();
  if (cat.includes("laser")) return "laser";
  if (cat.includes("lectrolyse")) return "electrolyse";
  if (cat.includes("regard") || name.includes("cil") || name.includes("brow")) return name.includes("brow") ? "brow" : "cil";
  return `custom:${s.category || "autres"}`;
}

export const Booking = ({ services }) => {
  const { t, tf } = useLang();
  const [family, setFamily] = useState(null);
  const [service, setService] = useState(null);
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const daysShort = t("daysShort");
  const months = t("months");
  const daysFull = t("days");
  const families = t("booking.families");

  const activeFamilies = [];
  FAMILY_META.forEach((f) => {
    if (services.some((s) => familyOf(s) === f.id)) {
      activeFamilies.push({ ...f, name: families[f.id].name, tag: families[f.id].tag });
    }
  });
  services.forEach((s) => {
    const f = familyOf(s);
    if (f.startsWith("custom:") && !activeFamilies.some((c) => c.id === f)) {
      activeFamilies.push({ id: f, img: IMAGES.salon, name: tf(`cat.${s.category}`, s.category || "Autres"), tag: "" });
    }
  });
  const familyServices = family ? services.filter((s) => familyOf(s) === family) : [];
  const currentFamily = activeFamilies.find((f) => f.id === family);

  useEffect(() => {
    if (!service) return;
    setLoadingDays(true);
    setDate(null);
    setTime(null);
    api
      .get("/availability", { params: { service_id: service.id, days: 14 } })
      .then((r) => setDays(r.data.days))
      .catch(() => toast.error(t("booking.errorAvail")))
      .finally(() => setLoadingDays(false));
  }, [service]);

  const selectedDay = days.find((d) => d.date === date);
  const dayLabel = (d) => {
    const di = FR_DAYS_IDX.indexOf(d.weekday.toLowerCase());
    const mi = FR_MONTHS_IDX.indexOf(d.month.toLowerCase());
    return { dow: di >= 0 ? daysShort[di] : d.weekday.slice(0, 3), mi };
  };
  const fullLabel = (d) => {
    const { mi } = dayLabel(d);
    const di = FR_DAYS_IDX.indexOf(d.weekday.toLowerCase());
    return `${di >= 0 ? daysFull[di] : d.weekday} ${d.day} ${mi >= 0 ? months[mi] : d.month}`;
  };

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/appointments", {
        service_id: service.id,
        date,
        time,
        name: form.name,
        phone: form.phone,
        message: form.message,
      });
      data.date_label = fullLabel(selectedDay);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e, t("booking.errorSlot")));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-taupe/30 rounded-md px-4 py-3.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300";

  return (
    <section id="rendez-vous" className="py-28 lg:py-36 bg-beige/60" data-testid="booking-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-[12px] tracking-[0.28em] uppercase text-taupe-dark mb-4">{t("booking.kicker")}</p>
          <h2 className="font-display tracking-tight text-4xl sm:text-5xl lg:text-6xl text-espresso leading-tight">
            {t("booking.titleA")} <span className="italic text-taupe">{t("booking.titleB")}</span>
          </h2>
          <p className="mt-4 text-base text-espresso/65 font-light max-w-xl">{t("booking.sub")}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 bg-white border border-taupe/20 rounded-3xl p-8 lg:p-12 shadow-[0_8px_30px_rgba(44,36,27,0.04)]">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  className="text-center py-10"
                  data-testid="booking-success"
                >
                  <div className="w-16 h-16 rounded-full bg-taupe/15 flex items-center justify-center mx-auto">
                    <Check className="w-7 h-7 text-taupe-dark" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-3xl text-espresso mt-6">{t("booking.successTitle")}</h3>
                  <p className="mt-3 text-sm text-espresso/65 font-light max-w-md mx-auto leading-relaxed">
                    {tf(`svcName.${result.appointment.service_name}`, result.appointment.service_name)} — {result.date_label} {t("booking.at")} {result.appointment.time}.{" "}
                    {t("booking.successText")}
                  </p>
                  <a
                    href={result.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-send-button"
                    className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#4A6B53] text-white px-8 py-4 text-sm tracking-[0.1em] uppercase hover:-translate-y-0.5 hover:brightness-110 transition-[transform,filter] duration-300"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                    {t("booking.successWa")}
                  </a>
                  <p className="mt-4 text-xs text-espresso/45">{t("booking.successNote")}</p>
                </motion.div>
              ) : (
                <motion.div key="flow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-12">
                  <div>
                    <StepLabel num="01" label={t("booking.stepType")} active />
                    <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="booking-families-grid">
                      {activeFamilies.map((f) => (
                        <button
                          key={f.id}
                          data-testid={`booking-family-${f.id}`}
                          onClick={() => { setFamily(f.id); setService(null); setDate(null); setTime(null); }}
                          className={`group relative overflow-hidden rounded-2xl border text-left transition-[border-color,transform] duration-300 hover:-translate-y-1 ${
                            family === f.id ? "border-taupe ring-2 ring-taupe/60" : "border-taupe/20 hover:border-taupe/60"
                          }`}
                        >
                          <div className="h-28 lg:h-32 overflow-hidden">
                            <img src={f.img} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                          <div className="p-4 bg-white">
                            <p className="font-display text-lg text-espresso leading-tight">{f.name}</p>
                            <p className="text-[11px] text-espresso/50 mt-1 leading-snug">{f.tag}</p>
                          </div>
                          {family === f.id && (
                            <span className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-taupe text-cream flex items-center justify-center">
                              <Check className="w-3.5 h-3.5" strokeWidth={2} />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {family && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                      <div className="flex items-center justify-between gap-4">
                        <StepLabel num="02" label={t("booking.stepService")} active />
                        <button
                          data-testid="booking-change-family"
                          onClick={() => { setFamily(null); setService(null); }}
                          className="text-[11px] tracking-[0.12em] uppercase text-taupe-dark hover:text-espresso transition-colors duration-300 inline-flex items-center gap-1.5"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> {currentFamily?.name}
                        </button>
                      </div>
                      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="booking-services-grid">
                        {familyServices.map((s) => (
                          <button
                            key={s.id}
                            data-testid={`booking-service-${s.id}`}
                            onClick={() => setService(s)}
                            className={`text-left border rounded-xl px-5 py-4 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                              service?.id === s.id ? "border-taupe bg-cream" : "border-taupe/20 bg-white hover:border-taupe/50"
                            }`}
                          >
                            <p className="text-[10px] tracking-[0.16em] uppercase text-taupe-dark">{currentFamily?.name}</p>
                            <p className="font-display text-lg text-espresso mt-1">{tf(`svcName.${s.name}`, s.name)}</p>
                            <p className="text-xs text-espresso/55 mt-1">{s.price_label} · {s.duration} min</p>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {service && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                      <StepLabel num="03" label={t("booking.step2")} active />
                      {loadingDays ? (
                        <div className="mt-5 flex items-center gap-3 text-sm text-espresso/55">
                          <Loader2 className="w-4 h-4 animate-spin" /> {t("booking.loading")}
                        </div>
                      ) : (
                        <div className="mt-5 flex gap-2.5 overflow-x-auto pb-2" data-testid="booking-dates-strip">
                          {days.map((d) => {
                            const hasSlots = d.open && d.times.length > 0;
                            const { dow, mi } = dayLabel(d);
                            return (
                              <button
                                key={d.date}
                                data-testid={`booking-date-${d.date}`}
                                disabled={!hasSlots}
                                onClick={() => { setDate(d.date); setTime(null); }}
                                className={`shrink-0 w-[76px] rounded-xl border px-2 py-3 text-center transition-[border-color,background-color] duration-300 ${
                                  date === d.date
                                    ? "border-taupe bg-taupe text-cream"
                                    : hasSlots
                                    ? "border-taupe/20 bg-white hover:border-taupe/60"
                                    : "border-taupe/10 bg-beige/40 text-espresso/30 cursor-not-allowed"
                                }`}
                              >
                                <p className="text-[10px] tracking-[0.1em] uppercase">{dow}</p>
                                <p className="font-display text-2xl mt-0.5">{d.day}</p>
                                <p className="text-[10px] uppercase">{mi >= 0 ? months[mi].slice(0, 4) : d.month.slice(0, 4)}</p>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {date && selectedDay && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                      <StepLabel num="04" label={t("booking.step3")} active />
                      <div className="mt-5 flex flex-wrap gap-2.5" data-testid="booking-slots-grid">
                        {selectedDay.times.map((tm) => (
                          <button
                            key={tm}
                            data-testid={`booking-slot-${tm}`}
                            onClick={() => setTime(tm)}
                            className={`rounded-full border px-5 py-2.5 text-sm transition-[border-color,background-color,color] duration-300 ${
                              time === tm
                                ? "border-espresso bg-espresso text-cream"
                                : "border-taupe/25 bg-white hover:border-taupe"
                            }`}
                          >
                            <Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />
                            {tm}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {time && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                      <StepLabel num="05" label={t("booking.step4")} active />
                      <div className="mt-5 grid sm:grid-cols-2 gap-4">
                        <input
                          data-testid="booking-name-input"
                          className={inputCls}
                          placeholder={t("booking.namePh")}
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <input
                          data-testid="booking-phone-input"
                          className={inputCls}
                          placeholder={t("booking.phonePh")}
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        />
                        <textarea
                          data-testid="booking-message-input"
                          className={`${inputCls} sm:col-span-2 min-h-[90px]`}
                          placeholder={t("booking.msgPh")}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                        />
                      </div>
                      <div className="mt-6 flex flex-wrap items-center gap-5">
                        <button
                          data-testid="booking-submit-button"
                          disabled={!form.name.trim() || !form.phone.trim() || sending}
                          onClick={submit}
                          className="rounded-full bg-taupe text-cream px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-taupe-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" strokeWidth={1.5} />}
                          {t("booking.submit")}
                        </button>
                        <p className="text-xs text-espresso/50 max-w-xs leading-relaxed">
                          {t("booking.recap")} {tf(`svcName.${service.name}`, service.name)} — {fullLabel(selectedDay)} {t("booking.at")} {time}.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
